import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import SessionTimes from '../../components/Mentors/SessionTimes';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, Badge } from '../../components/ui/Kit';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import {
  canManagePmEvents,
  getPmEventPreferredLiveUrl,
  getPmCustomDomainUrl,
  getStatusBadgeVariant,
  getPmEventAssigneeDisplay,
  PM_LANGUAGE_OPTIONS,
  PM_FORMAT_OPTIONS,
} from '../../lib/pmEventUtils';
import EventInvitationLinksPanel from './EventInvitationLinksPanel';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit2, FiExternalLink, FiTrash2, FiUsers, FiLink, FiImage, FiX } = FiIcons;

const TABS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'completed', label: 'Completed' },
  { key: 'archived', label: 'Archived' },
];

const PIPELINE_TYPE_OPTIONS = [
  { value: 'business_launch', label: 'Business Launch' },
  { value: 'free_summary_class', label: 'Free Summary Class' },
  { value: '__none__', label: 'Other / unset' },
];

/** Session timing for list tabs/sort. Uses pm_event_sessions.starts_at (app convention). */
function getEventTiming(event, nowMs = Date.now()) {
  const sessions = Array.isArray(event?.sessions) ? event.sessions : [];
  const timed = sessions
    .map((s) => {
      if (!s?.starts_at) return null;
      const t = new Date(s.starts_at).getTime();
      return Number.isFinite(t) ? { t, session: s } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.t - b.t);

  if (!timed.length) {
    return {
      hasSessions: false,
      isCompleted: false,
      upcomingStartsAt: null,
      latestStartsAt: null,
      displayAt: null,
      displaySession: null,
    };
  }

  const upcoming = timed.filter((x) => x.t >= nowMs);
  const isCompleted = upcoming.length === 0;
  const upcomingEntry = upcoming.length ? upcoming[0] : null;
  const latestEntry = timed[timed.length - 1];
  const displayEntry = isCompleted ? latestEntry : upcomingEntry;

  return {
    hasSessions: true,
    isCompleted,
    upcomingStartsAt: upcomingEntry?.t ?? null,
    latestStartsAt: latestEntry.t,
    displayAt: displayEntry?.t ?? null,
    displaySession: displayEntry?.session ?? null,
  };
}

function EventThumb({ url, title }) {
  const [broken, setBroken] = useState(false);
  const showImage = Boolean(url) && !broken;

  return (
    <div
      className="h-10 w-10 shrink-0 rounded-md overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center"
      aria-hidden={!showImage}
    >
      {showImage ? (
        <img
          src={url}
          alt=""
          width={40}
          height={40}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        <SafeIcon icon={FiImage} className="w-4 h-4 text-gray-400" title={title} />
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div className="min-w-[140px]">
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
      >
        <option value="">All</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

function matchesFilters(event, filters) {
  if (filters.format && event.format !== filters.format) return false;
  if (filters.language && event.language !== filters.language) return false;
  if (filters.pipeline_event_type) {
    if (filters.pipeline_event_type === '__none__') {
      if (event.pipeline_event_type) return false;
    } else if (event.pipeline_event_type !== filters.pipeline_event_type) {
      return false;
    }
  }
  return true;
}

function sortUpcoming(a, b) {
  const featuredA = a.is_main_event ? 1 : 0;
  const featuredB = b.is_main_event ? 1 : 0;
  if (featuredA !== featuredB) return featuredB - featuredA;

  const dateA = a._timing.upcomingStartsAt ?? Number.POSITIVE_INFINITY;
  const dateB = b._timing.upcomingStartsAt ?? Number.POSITIVE_INFINITY;
  if (dateA !== dateB) return dateA - dateB;

  return String(b.created_at || '').localeCompare(String(a.created_at || ''));
}

function sortCompleted(a, b) {
  const dateA = a._timing.latestStartsAt ?? 0;
  const dateB = b._timing.latestStartsAt ?? 0;
  if (dateA !== dateB) return dateB - dateA;
  return String(b.created_at || '').localeCompare(String(a.created_at || ''));
}

function sortArchived(a, b) {
  return String(b.created_at || '').localeCompare(String(a.created_at || ''));
}

export default function EventList() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const [filters, setFilters] = useState({
    format: '',
    language: '',
    pipeline_event_type: '',
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedInvitesEventId, setExpandedInvitesEventId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const canManage = canManagePmEvents(user);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const loadEvents = useCallback(async () => {
    if (!user?.agency_id) return;
    setLoading(true);
    try {
      const list = await api.pmEvents.getAll(user.agency_id);
      const detailed = await Promise.all(
        (list ?? []).map(async (row) => {
          try {
            const full = await api.pmEvents.getById(row.id);
            return {
              ...row,
              sessions: full?.sessions ?? [],
              session_count: full?.sessions?.length ?? row.session_count ?? 0,
            };
          } catch {
            return { ...row, sessions: [] };
          }
        }),
      );
      setEvents(detailed);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load events', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [user?.agency_id, addToast]);

  useEffect(() => {
    if (user?.agency_id) loadEvents();
  }, [user?.agency_id, loadEvents]);

  const enriched = useMemo(() => {
    const nowMs = Date.now();
    return events.map((event) => ({
      ...event,
      _timing: getEventTiming(event, nowMs),
    }));
  }, [events]);

  const tabCounts = useMemo(() => {
    const counts = { upcoming: 0, completed: 0, archived: 0 };
    for (const event of enriched) {
      if (event.status === 'archived') {
        counts.archived += 1;
      } else if (event.status === 'published' && event._timing.isCompleted) {
        counts.completed += 1;
      } else if (event.status === 'draft' || (event.status === 'published' && !event._timing.isCompleted)) {
        counts.upcoming += 1;
      }
    }
    return counts;
  }, [enriched]);

  const visibleEvents = useMemo(() => {
    const filtered = enriched.filter((event) => {
      if (!matchesFilters(event, filters)) return false;
      if (tab === 'archived') return event.status === 'archived';
      if (tab === 'completed') {
        return event.status === 'published' && event._timing.isCompleted;
      }
      // Upcoming: drafts + published not-yet-completed (exclude archived)
      if (event.status === 'archived') return false;
      if (event.status === 'draft') return true;
      if (event.status === 'published') return !event._timing.isCompleted;
      return false;
    });

    const sorted = [...filtered];
    if (tab === 'completed') sorted.sort(sortCompleted);
    else if (tab === 'archived') sorted.sort(sortArchived);
    else sorted.sort(sortUpcoming);
    return sorted;
  }, [enriched, filters, tab]);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await api.pmEvents.delete(deleteTarget.id);
      addToast({ type: 'success', title: 'Event deleted' });
      setDeleteTarget(null);
      if (expandedInvitesEventId === deleteTarget.id) {
        setExpandedInvitesEventId(null);
      }
      await loadEvents();
    } catch (err) {
      addToast({ type: 'error', title: 'Delete failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const toggleInvitesPanel = (eventId) => {
    setExpandedInvitesEventId((current) => (current === eventId ? null : eventId));
  };

  const copyLiveUrl = async (event) => {
    const url = getPmEventPreferredLiveUrl(event);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      addToast({ type: 'success', title: 'Live URL copied' });
    } catch {
      addToast({ type: 'error', title: 'Could not copy URL' });
    }
  };

  const resetFilters = () => {
    setFilters({ format: '', language: '', pipeline_event_type: '' });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  const emptyForTab =
    tab === 'completed'
      ? { title: 'No completed events', description: 'Published events whose session dates have passed will appear here.' }
      : tab === 'archived'
        ? { title: 'No archived events', description: 'Archived events will appear here.' }
        : {
            title: events.length ? 'No matching events' : 'No events yet',
            description: events.length
              ? 'Try clearing filters or check another tab.'
              : canManage
                ? 'Create your first workshop or seminar to publish a landing page.'
                : 'Ask a licensed agent or admin to create an event.',
          };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Prosperity Academy</h1>
          <p className="text-sm text-gray-500 mt-1">Create events, invitation links, and register attendees.</p>
        </div>
        {canManage && (
          <Button onClick={() => navigate('/mentors/new')}>
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
            New event
          </Button>
        )}
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              tab === t.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setTab(t.key)}
          >
            {t.label} ({tabCounts[t.key]})
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterSelect
          label="Format"
          value={filters.format}
          onChange={(v) => setFilters((f) => ({ ...f, format: v }))}
          options={PM_FORMAT_OPTIONS}
        />
        <FilterSelect
          label="Language"
          value={filters.language}
          onChange={(v) => setFilters((f) => ({ ...f, language: v }))}
          options={PM_LANGUAGE_OPTIONS}
        />
        <FilterSelect
          label="Type"
          value={filters.pipeline_event_type}
          onChange={(v) => setFilters((f) => ({ ...f, pipeline_event_type: v }))}
          options={PIPELINE_TYPE_OPTIONS}
        />
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={resetFilters}
            className="h-9 px-3 text-xs text-gray-500 hover:text-gray-800 border border-gray-300 rounded-md transition-colors flex items-center gap-1.5"
          >
            <SafeIcon icon={FiX} className="w-3 h-3" />
            Reset
          </button>
        )}
      </div>

      {!visibleEvents.length ? (
        <EmptyState
          title={emptyForTab.title}
          description={emptyForTab.description}
          actionLabel={
            tab === 'upcoming' && !events.length && canManage
              ? 'New event'
              : activeFilterCount > 0
                ? 'Clear filters'
                : undefined
          }
          onAction={
            tab === 'upcoming' && !events.length && canManage
              ? () => navigate('/mentors/new')
              : activeFilterCount > 0
                ? resetFilters
                : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Event</th>
                  <th className="px-4 py-3 font-medium">Assignee</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">
                    {tab === 'completed' ? 'Completed' : 'Date'}
                  </th>
                  <th className="px-4 py-3 font-medium">Sessions</th>
                  <th className="px-4 py-3 font-medium">Registrations</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleEvents.map((event) => {
                  const invitesExpanded = expandedInvitesEventId === event.id;
                  const assignee = getPmEventAssigneeDisplay(event);
                  const displaySession = event._timing.displaySession;
                  return (
                    <Fragment key={event.id}>
                      <tr className={`hover:bg-gray-50/80 ${invitesExpanded ? 'bg-gray-50/60' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-3">
                            <Link
                              to={`/mentors/${event.id}/registrations`}
                              className="shrink-0"
                              title="Registrations"
                            >
                              <EventThumb url={event.hero_image_url} title={event.title} />
                            </Link>
                            <div className="min-w-0">
                              <Link
                                to={`/mentors/${event.id}/registrations`}
                                className="font-medium text-gray-900"
                                title="Registrations"
                              >
                                {event.title}
                              </Link>
                              <div className="text-xs text-gray-500">/{event.slug}</div>
                              {event.custom_domain && (
                                <a
                                  href={getPmCustomDomainUrl(event.custom_domain) || `https://${event.custom_domain}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-xs text-primary hover:underline"
                                  title="Custom domain"
                                >
                                  {event.custom_domain}
                                </a>
                              )}
                              <div className="flex flex-wrap gap-1 mt-1">
                                {event.is_main_event && (
                                  <Badge variant="primary">Featured</Badge>
                                )}
                                {event.pipeline_event_type === 'business_launch' && (
                                  <Badge variant="warning">Business Launch</Badge>
                                )}
                                {event.pipeline_event_type === 'free_summary_class' && (
                                  <Badge variant="teal">Free Summary Class</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {assignee.name ? (
                            <div className="min-w-0">
                              <div className="truncate">{assignee.name}</div>
                              {assignee.source === 'manual' && (
                                <div className="text-xs text-gray-400">Not in system yet</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={getStatusBadgeVariant(event.status)}>{event.status}</Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-700 min-w-[14rem]">
                          {displaySession ? (
                            <SessionTimes
                              startsAt={displaySession.starts_at}
                              endsAt={displaySession.ends_at}
                              timezone={displaySession.timezone}
                            />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{event.session_count ?? 0}</td>
                        <td className="px-4 py-3 text-gray-700">{event.registration_count ?? 0}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Link
                              to={`/mentors/${event.id}`}
                              className="p-2 text-gray-500 hover:text-primary rounded"
                              title="Edit"
                            >
                              <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                            </Link>
                            <button
                              type="button"
                              onClick={() => toggleInvitesPanel(event.id)}
                              className={`p-2 rounded ${
                                invitesExpanded
                                  ? 'text-primary bg-primary/10'
                                  : 'text-gray-500 hover:text-primary'
                              }`}
                              title="Invitation links"
                              aria-expanded={invitesExpanded}
                            >
                              <SafeIcon icon={FiLink} className="w-4 h-4" />
                            </button>
                            <Link
                              to={`/mentors/${event.id}/registrations`}
                              className="p-2 text-gray-500 hover:text-primary rounded"
                              title="Registrations"
                            >
                              <SafeIcon icon={FiUsers} className="w-4 h-4" />
                            </Link>
                            {event.status === 'published' && (
                              <button
                                type="button"
                                onClick={() => copyLiveUrl(event)}
                                className="p-2 text-gray-500 hover:text-primary rounded"
                                title={
                                  event.custom_domain
                                    ? `Copy live URL (${event.custom_domain})`
                                    : 'Copy live URL'
                                }
                              >
                                <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                              </button>
                            )}
                            {canManage && (
                              <button
                                type="button"
                                onClick={() => setDeleteTarget(event)}
                                className="p-2 text-gray-500 hover:text-red-600 rounded"
                                title="Delete"
                              >
                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {invitesExpanded && (
                        <tr className="bg-white">
                          <td colSpan={7} className="px-4 pb-4 pt-0 border-t border-gray-100">
                            <div className="pt-3 max-w-2xl">
                              <div className="text-sm font-medium text-gray-900 mb-3">
                                Invitation links — {event.title}
                              </div>
                              <EventInvitationLinksPanel
                                eventId={event.id}
                                eventSlug={event.slug}
                                customDomain={event.custom_domain}
                                agencyId={event.agency_id || user?.agency_id}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete event?"
      >
        <p className="text-sm text-gray-600 mb-4">
          Delete <strong>{deleteTarget?.title}</strong>? Sessions, speakers, invites, and registrations
          for this event will be removed.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={actionLoading}>
            {actionLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
